import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { FileBarChart, IndianRupee, BarChart3, BookOpen, CreditCard, AlertTriangle, TrendingDown, Users, Package, Briefcase, Award, Search, Sprout, ClipboardList, FileText, FolderKanban, Wallet } from 'lucide-react';
import { useAuth } from '../AuthContext';

const Reports = () => {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();

  const canLedger = ['Admin', 'Account'].includes(user?.role);

  const reports = [
    { id: 'credit-history', title: 'Credit History Search', description: 'Search individual credit history by Name, Mobile, PAN or Aadhaar', icon: Search, color: 'emerald', path: '/reports/credit-history' },
    { id: 'statistics', title: 'Statistics Report', description: 'Comprehensive overview of all BRC metrics and KPIs', icon: BarChart3, color: 'emerald', path: '/reports/statistics' },
    { id: 'cashbook', title: 'Cashbook', description: 'Detailed record of all cash receipts and payments', icon: IndianRupee, color: 'blue', path: '/reports/cashbook' },
    { id: 'credit-report', title: 'Entrepreneur Credit Report', description: 'Repayment history, missed EMIs, creditworthiness for 2nd loan eligibility', icon: Users, color: 'red', path: '/reports/credit-report' },
    { id: 'loan-emi', title: 'Loan EMI Report', description: 'Summary of all loan EMI schedules', icon: FileBarChart, color: 'purple', path: '/reports/loan-emi' },
    { id: 'loan-ledger', title: 'Loan Ledger', description: 'Date-wise ledger with principal & interest breakdown', icon: BookOpen, color: 'amber', path: '/reports/loan-ledger' },
    { id: 'dcb', title: 'Demand Collection Book', description: 'Monthly DCB report with demand, collection & overdue tracking', icon: CreditCard, color: 'rose', path: '/reports/dcb' },
    { id: 'par', title: 'PAR Report', description: 'Portfolio at Risk - 30/90/180+ days overdue', icon: AlertTriangle, color: 'red', path: '/reports/par' },
    { id: 'repayment-npa', title: 'Repayment & NPA Report', description: 'Individual & branch-wise repayment %, NPA & defaulters', icon: TrendingDown, color: 'slate', path: '/reports/repayment-npa' },
    { id: 'crp-ep', title: 'CRP-EP Report', description: 'Entrepreneurs & loans grouped by CRP-EP promoter', icon: Users, color: 'purple', path: '/reports/crp-ep' },
    { id: 'asset-register', title: 'Asset Register', description: 'Branch-wise asset list with depreciation tracking', icon: Package, color: 'teal', path: '/reports/asset-register' },
    { id: 'branch-comparison', title: 'Branch Comparison', description: 'Compare branches — targets, staff, enterprises & CEF / CIF / Bank loans', icon: BarChart3, color: 'indigo', path: '/reports/branch-comparison' },
    { id: 'grounded-enterprises', title: 'Grounded Enterprises', description: 'Enterprises with CEF, CIF, or Bank loans', icon: Briefcase, color: 'orange', path: '/reports/grounded-enterprises' },
    { id: 'loan-certificates', title: 'Loan Closure Certificates', description: 'Generate closure certificates for fully repaid loans', icon: Award, color: 'amber', path: '/reports/loan-certificates' },
    { id: 'financial-summary', title: 'Financial Summary', description: 'Funds received, Loans disbursed, Interest earned & Expenditures', icon: IndianRupee, color: 'teal', path: '/reports/financial-summary' },
    { id: 'project-expenditure', title: 'Project-wise Expenditure', description: 'Expenditure grouped by Project Activity and Block with drill-down and print/CSV export', icon: FolderKanban, color: 'orange', path: '/reports/project-expenditure' },
    { id: 'all-entrepreneurs', title: 'All Entrepreneurs', description: 'Complete list of every entrepreneur across all branches with print, Excel & PDF export', icon: Briefcase, color: 'emerald', path: '/reports/all-entrepreneurs' },
    { id: 'business-plan-report', title: '1A. Business Plan', description: 'List of business plans with status, drill-down and re-print/export of any approved plan', icon: ClipboardList, color: 'emerald', path: '/reports/business-plans' },
    { id: 'loan-application-report', title: '1B. Loan Application', description: 'Loan applications generated from approved business plans (status, amount, branch)', icon: FileText, color: 'blue', path: '/reports/loan-applications' },
    { id: 'livelihoods', title: 'Livelihoods Cell Report', description: 'Consolidated report: Skill Training, Products (MSME/UDYAM), Marketing & Financial Support', icon: Sprout, color: 'emerald', path: '/reports/livelihoods' },
    ...(canLedger ? [{ id: 'member-ledger', title: 'Member Ledger Report', description: 'Admin/Account only — view & print any member or staff ledger with Dr/Cr, running balance and date range filter', icon: BookOpen, color: 'emerald', path: '/reports/member-ledger' }] : []),
    ...(isAdmin() ? [{ id: 'all-ledgers', title: 'All Ledgers (Branch + Member)', description: 'Admin-only consolidated report — totals per branch ledger and every member ledger with grand-total Dr/Cr, date filter, print & CSV export', icon: Wallet, color: 'emerald', path: '/reports/all-ledgers' }] : []),
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
      blue: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
      purple: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100',
      slate: 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100',
      amber: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
      rose: 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100',
      red: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
      teal: 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100',
      indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100',
      orange: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100',
    };
    return colors[color] || colors.emerald;
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="reports-page">
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1">Reports</h1>
        <p className="text-sm text-slate-600">Generate and view financial reports</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {reports.filter(r => !r.adminOnly || isAdmin()).map((report) => {
          const Icon = report.icon;
          const colorClasses = getColorClasses(report.color);

          return (
            <div
              key={report.id}
              data-testid={`report-card-${report.id}`}
              className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 hover:shadow-md transition-all"
            >
              <div className={`w-10 h-10 rounded-lg ${colorClasses} flex items-center justify-center mb-3`}>
                <Icon className="w-5 h-5" strokeWidth={2} />
              </div>

              <h3 className="text-sm font-semibold text-slate-900 mb-1">{report.title}</h3>
              <p className="text-xs text-slate-600 mb-3 line-clamp-2">{report.description}</p>

              <Button
                onClick={() => navigate(report.path)}
                data-testid={`view-${report.id}-button`}
                size="sm"
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white h-8 text-xs"
              >
                Open
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Reports;
